import type { ErrorResponse } from "@apollo/client/link/error";
import type { Breadcrumb, Scope } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { GraphQLError } from "graphql/error";
import type { Kind } from "graphql/language";
import { logToSentry } from "./logger";

// What the packages report to Sentry about failed GraphQL operations. Setting Sentry up
// (initSentry and the event normalisation) is the app's business.

type SentryErrorEnrichedData = {
	kind: string;
	query: Kind.DOCUMENT;
	variables: Record<string, string | number>;
};

// Collect data about fridge and GQL query for Sentry scope
const getErrorData = (error: ErrorResponse): SentryErrorEnrichedData => ({
	kind: error.operation.operationName,
	query: error.operation.query.kind,
	variables: error.operation.variables,
});

const getBreadcrumb = (graphQlError: GraphQLError): Breadcrumb => ({
	category: "query-path",
	message: graphQlError.path?.join(" > "),
});

// Apollo hands us values that are NOT Error instances: the AppSync realtime ws-drop arrives as a plain
// `{ errors: [{ message }] }` object, and graphQLErrors can be plain serialized objects too. Pull the
// human-readable message out of those shapes.
const extractErrorMessage = (value: unknown): string | undefined => {
	if (typeof value === "string") {
		return value;
	}
	if (value && typeof value === "object") {
		const maybe = value as { message?: unknown; errors?: unknown };
		if (typeof maybe.message === "string") {
			return maybe.message;
		}
		if (Array.isArray(maybe.errors) && maybe.errors.length > 0) {
			const first = maybe.errors[0] as { message?: unknown };
			if (typeof first?.message === "string") {
				return first.message;
			}
		}
	}
	return undefined;
};

// Sentry.captureException needs a real Error. A non-Error (plain object) makes Sentry synthesize the
// useless "Object captured as exception with keys: ..." title and collapse distinct errors into one
// issue. Coerce into a named Error with a meaningful message so the event is titled + grouped properly.
const toError = (value: unknown, name: string): Error => {
	if (value instanceof Error) {
		return value;
	}
	const error = new Error(extractErrorMessage(value) ?? "Unknown Apollo error");
	error.name = name;
	return error;
};

// What the scope learns about the failing operation: its kind and fridge as tags, then its query, its variables and
// the caller's extra data as extras, in this order (a later extra replaces an earlier one of the same key).
const setOperationContext = (
	scope: Scope,
	errorData: SentryErrorEnrichedData,
	extraData: { [key: string]: unknown } | undefined,
) => {
	if (errorData.kind) {
		scope.setTag("kind", errorData.kind);
	}
	// fridgeId is already a global tag from initSentry, but pin it from the failing operation's
	// variables too so it is guaranteed present and filterable on these events.
	if (errorData.variables?.id) {
		scope.setTag("fridgeId", String(errorData.variables.id));
	}
	if (errorData.query) {
		scope.setExtra("query", errorData.query);
	}
	if (errorData.variables) {
		scope.setExtra("variables", errorData.variables);
	}
	if (extraData) {
		for (const key of Object.keys(extraData)) {
			scope.setExtra(key, extraData[key]);
		}
	}
};

export const captureApolloError = (
	error: unknown,
	errorData: SentryErrorEnrichedData,
	extraData?: { [key: string]: unknown },
) => {
	Sentry.withScope((scope) => {
		setOperationContext(scope, errorData, extraData);
		if (error instanceof GraphQLError && error?.path) {
			scope.addBreadcrumb(getBreadcrumb(error));
		}
		const capturedError = toError(error, `${errorData.kind}Error`);
		// Keep the raw payload when we had to synthesize an Error so nothing is lost.
		if (capturedError !== error) {
			scope.setExtra("originalError", error);
		}
		Sentry.captureException(capturedError);
	});
};

export const handleSendApolloErrorToSentry = (error: ErrorResponse) => {
	const errorData = getErrorData(error);

	if (error.graphQLErrors) {
		for (const graphQlError of error.graphQLErrors) {
			// Pass the GraphQLError through untouched — spreading it into a plain object stripped the
			// Error prototype and produced the generic "Object captured..." title. captureApolloError
			// now coerces non-Errors itself.
			captureApolloError(graphQlError, errorData);
		}
		return;
	}

	if (error.networkError) {
		captureApolloError(error.networkError, errorData);
		return;
	}
	logToSentry("Application error", "error", [JSON.stringify(error)]);
};
